import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Trash2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WEBHOOK_CHANNELS = new Set(["WhatsApp", "Email"]);

export default function ChannelDeleteDialog({ open, onOpenChange, channel: target, onDeleted }) {
  const { user } = useAuth();
  const canHard = user?.is_super_admin;
  const [mode, setMode] = useState("soft");
  const [typed, setTyped] = useState("");
  const [ticketCount, setTicketCount] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) { setMode("soft"); setTyped(""); setTicketCount(null); }
  }, [open]);

  useEffect(() => {
    if (open && target?.id) {
      api.get(`/channels/${target.id}/ticket-count`)
        .then((r) => setTicketCount(r.data.ticket_count))
        .catch(() => setTicketCount(0));
    }
  }, [open, target]);

  if (!target) return null;
  const isWebhookWired = WEBHOOK_CHANNELS.has(target.name);
  const canConfirm = mode === "soft" || typed.trim().toLowerCase() === target.name.toLowerCase();

  const submit = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      const url = mode === "hard" ? `/channels/${target.id}?hard=true` : `/channels/${target.id}`;
      await api.delete(url);
      toast.success(mode === "hard" ? "Channel permanently deleted" : `${target.name} deactivated`);
      onOpenChange(false);
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="delete-channel-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-500" />
            {mode === "hard" ? "Permanently delete channel" : "Deactivate channel"}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {mode === "soft"
              ? "Channel will be hidden from the new-ticket dropdown. Historical tickets keep their channel tag intact."
              : "Permanent delete removes the channel row. Historical tickets keep their channel string but lose icon/colour metadata."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 text-sm">
          <div className="bg-slate-50 border border-slate-200 rounded p-3 flex items-center justify-between">
            <div>
              <div className="text-xs text-slate-500">Channel</div>
              <div className="font-medium text-slate-800">{target.name}</div>
              {ticketCount !== null && (
                <div className="text-xs text-slate-500 mt-0.5">
                  {ticketCount} ticket{ticketCount === 1 ? "" : "s"} reference{ticketCount === 1 ? "s" : ""} this channel
                </div>
              )}
            </div>
            {isWebhookWired && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1">
                <Link2 className="w-3 h-3" /> Webhook
              </span>
            )}
          </div>

          {isWebhookWired && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Heads up:</strong> {target.name} receives inbound traffic via webhook. {mode === "soft"
                  ? "Deactivating only hides it from the new-ticket dropdown — inbound messages will still arrive and be tagged with this channel name."
                  : "Permanently deleting won't stop incoming webhook traffic — those messages will still create tickets with this channel string but the channel master row will be gone."}
              </span>
            </div>
          )}

          {mode === "hard" && (
            <div className="space-y-1.5">
              <Label className="text-xs text-rose-700">
                Type <span className="font-mono font-semibold">{target.name}</span> to confirm
              </Label>
              <Input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={target.name}
                className="font-mono text-xs"
                data-testid="channel-delete-confirm-input"
                autoFocus
              />
            </div>
          )}

          {canHard && (
            <button
              type="button"
              onClick={() => { setMode(mode === "soft" ? "hard" : "soft"); setTyped(""); }}
              className="text-xs text-rose-600 hover:text-rose-700 underline"
              data-testid="channel-toggle-permanent"
            >
              {mode === "soft" ? "I need to permanently delete instead →" : "← Back to soft deactivate"}
            </button>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={!canConfirm || submitting}
            className={mode === "hard" ? "bg-rose-600 hover:bg-rose-700" : "bg-slate-700 hover:bg-slate-800"}
            data-testid="channel-delete-confirm-btn"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            {submitting ? "Working..." : (mode === "hard" ? "Permanently delete" : "Deactivate")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
